import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import AuthService from '../../components/services/AuthService';
import IElementUIsService from '../../components/services/interfaces/ElementUIsService';
import { ElementUI, UserInfo } from '../../types/interfaces';
import { AuthRequest } from '../../types/interfaces/AuthRequest';

interface AuthState {
  isAuth: boolean;
  twoFactorEnabled: boolean;
  isLoaded: boolean;
  userInfo: UserInfo;
  elementUIs: ElementUI[];
  errorMessage: string;
}

const initialState: AuthState = {
  isAuth: false,
  twoFactorEnabled: false,
  isLoaded: false,
  userInfo: { login: '', departments: [], displayName: '', isFirstLogin: false },
  errorMessage: '',
  elementUIs: []
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<boolean>) => {
      state.isAuth = action.payload;
    },
    setIsLoaded: (state, action: PayloadAction<boolean>) => {
      state.isLoaded = action.payload;
    },
    // TODO added for testing
    setElementUIs: (state, action: PayloadAction<ElementUI[]>) => {
      state.elementUIs = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(
      loginAsync.fulfilled,
      (
        state,
        action: PayloadAction<{ hasError: boolean; twoFactorEnabled: boolean }>
      ) => {
        const payload = action.payload;
        state.isAuth = !payload.hasError && !payload.twoFactorEnabled;
        state.twoFactorEnabled = payload.twoFactorEnabled;
        if (payload.hasError)
          state.errorMessage = 'Введены неверные учетные данные.';
        else state.errorMessage = '';
      }
    );
    builder.addCase(
      loginWithCodeAsync.fulfilled,
      (state, action: PayloadAction<boolean>) => {
        const payload = action.payload;
        state.isAuth = payload;
        if (!payload) state.errorMessage = 'Введены неверные учетные данные.';
        else state.errorMessage = '';
      }
    );
    builder.addCase(logoutAsync.fulfilled, (state) => {
      state.isAuth = false;
      state.twoFactorEnabled = false;
    });
    builder.addCase(
      refreshAsync.fulfilled,
      (state, action: PayloadAction<boolean>) => {
        state.isAuth = action.payload;
      }
    );
    builder.addCase(
      getUserAsync.fulfilled,
      (state, action: PayloadAction<UserInfo>) => {
        state.userInfo = action.payload;
      }
    );
    builder.addCase(
      getCurrentUserElementCodes.fulfilled,
      (state, action: PayloadAction<ElementUI[]>) => {
        state.elementUIs = action.payload;
      }
    );
  }
});

export const loginAsync = createAsyncThunk(
  'auth/loginAsync',
  async (data: AuthRequest) => {
    try {
      const response = await AuthService.login(data.username, data.password);
      if (response.data.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
        return { hasError: false, twoFactorEnabled: false };
      }

      if (
        response.data.accessToken === '' &&
        response.data.information === 'TwoFactorNeeded'
      ) {
        return { hasError: false, twoFactorEnabled: true };
      }

      return { hasError: true, twoFactorEnabled: false };
    } catch (e) {
      return { hasError: true, twoFactorEnabled: false };
    }
  }
);

export const loginWithCodeAsync = createAsyncThunk(
  'auth/loginWithCodeAsync',
  async (data: AuthRequest) => {
    try {
      const response = await AuthService.loginWithCode(
        data.username,
        data.password,
        data.code ?? ''
      );
      if (response.data.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }
);

export const logoutAsync = createAsyncThunk('auth/logoutAsync', async () => {
  try {
    await AuthService.logout();
    localStorage.removeItem('token');
  } catch (e) {}
});

export const refreshAsync = createAsyncThunk('auth/refreshAsync', async () => {
  try {
    const response = await AuthService.refresh();
    if (response.data.accessToken) {
      localStorage.setItem('token', response.data.accessToken);
      return true;
    } else return false;
  } catch (e) {
    return false;
  }
});

export const getUserAsync = createAsyncThunk('auth/getUserAsync', async () => {
  try {
    const response = await AuthService.getUser();
    return response.data.value;
  } catch (e) {
    return initialState.userInfo;
  }
});

export const getCurrentUserElementCodes = createAsyncThunk(
  'auth/getCurrentUserElementCodes',
  async (elementUIsService: IElementUIsService) => {
    try {
      const response =
        await elementUIsService.getCurrentUserElementCodes().result;
      return response;
    } catch (e) {
      return initialState.elementUIs;
    }
  }
);

export const { setAuth, setIsLoaded, setElementUIs } = authSlice.actions;

export default authSlice.reducer;
